import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit2, Power, MapPin, RefreshCw, Settings, Search, Zap, Eye } from 'lucide-react';
import smartThingsService from '../services/smartThingsService';
import smartThingsBackendService from '../services/smartThingsBackendService';
import { IoTDevice, IoTRoom, DEVICE_TYPES, DEVICE_TYPE_LABELS, inferDeviceType } from '../models/iotModels';
import { useAuth } from '../contexts/AuthContext';
import './IoTManagement.css';

const DEFAULT_WEBHOOK_URL = process.env.REACT_APP_SMARTTHINGS_WEBHOOK_URL || '';
// SmartThings API token lives on the BACKEND only — never inline a server credential
// into a React build (REACT_APP_* gets baked into the production JS bundle).
// All SmartThings calls go through smartThingsService → backend `/smartthings/*`.

const IoTManagement = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [configError, setConfigError] = useState(false);
  const [hotelInfo, setHotelInfo] = useState(null);

  // Modal states
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  // Form states
  const [roomForm, setRoomForm] = useState({ name: '', hotelRoomNumber: '', floor: '' });
  const [deviceForm, setDeviceForm] = useState({
    deviceId: '',
    name: '',
    type: DEVICE_TYPES.OTHER,
    roomId: ''
  });

  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    name: '',
    deviceId: '',
    componentId: 'main',
    capability: 'switch',
    attribute: 'switch',
    stateChangeOnly: true,
    value: '*',
    targetUrl: DEFAULT_WEBHOOK_URL
  });
  const [savingSubscription, setSavingSubscription] = useState(false);

  // Load initial data — backend's /smartthings/* endpoints will surface a clear
  // error if the server-side SmartThings token is missing or invalid.
  useEffect(() => {
    if (currentUser && userProfile) {
      loadLocations();
    } else {
      console.log('⏳ Waiting for user authentication before loading SmartThings locations...');
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (selectedLocation) {
      loadRoomsAndDevices();
    }
  }, [selectedLocation]);

  useEffect(() => {
    // Only load subscriptions if user is authenticated
    if (currentUser) {
      loadSubscriptions();
    }
  }, [currentUser]);

  const loadLocations = async () => {
    setLoading(true);
    try {
      // Check if user is a manager - only show their hotel's location
      if (userProfile?.role === 'manager') {
        const result = await smartThingsService.getMyLocation();
        if (result.success && result.data?.location) {
          setLocations([result.data.location]);
          setSelectedLocation(result.data.location.locationId);
          setHotelInfo(result.data.hotel);
          console.log('📍 Manager location loaded:', result.data.hotel?.name);
        } else {
          // No location configured yet for this hotel
          setLocations([]);
          setSelectedLocation(null);
          setHotelInfo(result.data?.hotel);
          if (result.data?.message) {
            toast.info(result.data.message);
          }
        }
      } else {
        // Admin or other roles - show all locations
        const locs = await smartThingsService.getLocations();
        setLocations(locs);
        if (locs.length > 0) {
          setSelectedLocation(locs[0].locationId);
        }
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Failed to load SmartThings locations. Please check your API token configuration.');
      // Don't throw - just show error and continue
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRoomsAndDevices = async () => {
    setLoading(true);
    try {
      const [roomsData, devicesData] = await Promise.all([
        smartThingsService.getRooms(selectedLocation),
        smartThingsService.getAllDevices(selectedLocation)
      ]);

      const roomInstances = roomsData.map(r => new IoTRoom(r));
      setRooms(roomInstances);

      const deviceInstances = await Promise.all(
        devicesData.map(async (d) => {
          const status = await smartThingsService.getDeviceStatus(d.deviceId).catch(() => ({}));
          const deviceType = inferDeviceType(d.components?.main?.capabilities || []);
          return new IoTDevice({ ...d, deviceType, state: status });
        })
      );
      setDevices(deviceInstances);
    } catch (error) {
      console.error('Failed to load rooms and devices:', error);
      toast.error('Failed to load SmartThings data. Please check your configuration.');
      // Set empty arrays instead of throwing
      setRooms([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    // Don't attempt to load if no user is authenticated
    if (!currentUser) {
      setSubscriptions([]);
      return;
    }

    setSubscriptionsLoading(true);
    try {
      const subs = await smartThingsBackendService.getSubscriptions();
      setSubscriptions(subs);
    } catch (error) {
      // Silently handle auth errors - subscriptions are optional
      if (error.response?.status === 401) {
        console.log('Subscriptions require authentication. Please log in.');
      } else {
        console.error('Failed to load subscriptions:', error);
      }
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const refreshDevices = async () => {
    setRefreshing(true);
    await loadRoomsAndDevices();
    setRefreshing(false);
    toast.success('Devices refreshed');
  };

  const resolveSubscriptionId = (subscription) => {
    return subscription?.id || subscription?.subscriptionId || subscription?.subscriptionName;
  };

  const resetSubscriptionForm = () => {
    setSubscriptionForm({
      name: '',
      deviceId: '',
      componentId: 'main',
      capability: 'switch',
      attribute: 'switch',
      stateChangeOnly: true,
      value: '*',
      targetUrl: DEFAULT_WEBHOOK_URL
    });
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();

    if (!subscriptionForm.deviceId || !subscriptionForm.capability || !subscriptionForm.attribute) {
      toast.error('Device ID, capability, and attribute are required');
      return;
    }

    const targetUrl = subscriptionForm.targetUrl || DEFAULT_WEBHOOK_URL;
    if (!targetUrl) {
      toast.error('Set SMARTTHINGS_WEBHOOK_URL or provide a target URL');
      return;
    }

    setSavingSubscription(true);
    try {
      await smartThingsBackendService.createSubscription({
        name: subscriptionForm.name || undefined,
        deviceId: subscriptionForm.deviceId,
        componentId: subscriptionForm.componentId || 'main',
        capability: subscriptionForm.capability,
        attribute: subscriptionForm.attribute,
        stateChangeOnly: !!subscriptionForm.stateChangeOnly,
        value: subscriptionForm.value || '*',
        targetUrl
      });

      toast.success('Subscription created');
      setShowAddSubscriptionModal(false);
      resetSubscriptionForm();
      loadSubscriptions();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Unknown error';
      toast.error('Failed to create subscription: ' + message);
    } finally {
      setSavingSubscription(false);
    }
  };

  const handleDeleteSubscription = async (subscription) => {
    const subscriptionId = resolveSubscriptionId(subscription);
    if (!subscriptionId) {
      toast.error('Unable to determine subscription ID');
      return;
    }

    if (!window.confirm('Remove this SmartThings subscription?')) {
      return;
    }

    try {
      await smartThingsBackendService.deleteSubscription(subscriptionId);
      toast.success('Subscription removed');
      loadSubscriptions();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Unknown error';
      toast.error('Failed to delete subscription: ' + message);
    }
  };

  // Room Management
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      const roomData = {
        name: roomForm.name,
        metadata: {
          hotelRoomNumber: roomForm.hotelRoomNumber,
          floor: roomForm.floor
        }
      };
      await smartThingsService.createRoom(selectedLocation, roomData);
      toast.success('Room created successfully');
      setShowAddRoomModal(false);
      setRoomForm({ name: '', hotelRoomNumber: '', floor: '' });
      loadRoomsAndDevices();
    } catch (error) {
      toast.error('Failed to create room: ' + error.message);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room? Devices will become unassigned.')) {
      return;
    }

    try {
      await smartThingsService.deleteRoom(selectedLocation, roomId);
      toast.success('Room deleted successfully');
      loadRoomsAndDevices();
    } catch (error) {
      toast.error('Failed to delete room: ' + error.message);
    }
  };

  // Device Management
  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        label: deviceForm.name,
        roomId: deviceForm.roomId || null
      };
      await smartThingsService.updateDevice(deviceForm.deviceId, updates);
      toast.success('Device added successfully');
      setShowAddDeviceModal(false);
      setDeviceForm({ deviceId: '', name: '', type: DEVICE_TYPES.OTHER, roomId: '' });
      loadRoomsAndDevices();
    } catch (error) {
      toast.error('Failed to add device: ' + error.message);
    }
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      deviceId: device.id,
      name: device.name,
      type: device.type,
      roomId: device.roomId || ''
    });
    setShowEditDeviceModal(true);
  };

  const handleUpdateDevice = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        label: deviceForm.name,
        roomId: deviceForm.roomId || null
      };
      await smartThingsService.updateDevice(deviceForm.deviceId, updates);
      toast.success('Device updated successfully');
      setShowEditDeviceModal(false);
      setEditingDevice(null);
      loadRoomsAndDevices();
    } catch (error) {
      toast.error('Failed to update device: ' + error.message);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to remove this device?')) {
      return;
    }

    try {
      await smartThingsService.deleteDevice(deviceId);
      toast.success('Device removed successfully');
      loadRoomsAndDevices();
    } catch (error) {
      toast.error('Failed to remove device: ' + error.message);
    }
  };

  const handleToggleDevice = async (device) => {
    try {
      const currentState = device.getState('switch', 'switch');
      if (currentState === 'on') {
        await smartThingsService.turnOffDevice(device.id);
        toast.success(`${device.name} turned off`);
      } else {
        await smartThingsService.turnOnDevice(device.id);
        toast.success(`${device.name} turned on`);
      }
      setTimeout(loadRoomsAndDevices, 1000);
    } catch (error) {
      toast.error('Failed to toggle device: ' + error.message);
    }
  };

  // Filtering
  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || device.type === filterType;
    const matchesRoom = !selectedRoom || device.roomId === selectedRoom;
    return matchesSearch && matchesType && matchesRoom;
  });

  const getDevicesByRoom = (roomId) => {
    return devices.filter(d => d.roomId === roomId);
  };

  if (loading && devices.length === 0) {
    return (
      <div className="iot-management-loading">
        <div className="spinner-large"></div>
        <p>Loading IoT devices...</p>
      </div>
    );
  }

  return (
    <div className="iot-management-container">
      {/* Configuration Error Warning */}
      {configError && (
        <div className="alert alert-error" style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          color: '#c33'
        }}>
          <h3>⚠️ SmartThings Configuration Missing</h3>
          <p>
            The SmartThings API token is not configured on the backend. Please ask your administrator to set
            <code style={{background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px', margin: '0 4px'}}>
              SMARTTHINGS_PAT
            </code> in the backend environment (never in the frontend — server credentials must not ship in the JS bundle).
          </p>
        </div>
      )}
      
      <div className="iot-management-header">
        <div className="header-left">
          <h1>🏠 IoT Device Management</h1>
          <p className="subtitle">
            {hotelInfo ? (
              <>Managing smart devices for <strong>{hotelInfo.name}</strong></>
            ) : (
              'Configure and manage smart devices across your property'
            )}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-icon" onClick={refreshDevices} disabled={refreshing || configError}>
            <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
          </button>
          <button className="btn-primary" onClick={() => setShowAddRoomModal(true)} disabled={configError}>
            <Plus size={18} />
            Add Room
          </button>
          <button className="btn-primary" onClick={() => setShowAddDeviceModal(true)} disabled={configError}>
            <Plus size={18} />
            Add Device
          </button>
        </div>
      </div>

      {/* Location Selector - Show only for admins with multiple locations */}
      {userProfile?.role !== 'manager' && locations.length > 1 && (
        <div className="location-selector">
          <label>Location:</label>
          <select
            value={selectedLocation || ''}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="form-select"
          >
            {locations.map(loc => (
              <option key={loc.locationId} value={loc.locationId}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* No Location Message for Managers */}
      {userProfile?.role === 'manager' && !selectedLocation && !loading && (
        <div className="alert alert-info" style={{
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          color: '#0066cc'
        }}>
          <h3>📍 No SmartThings Location Configured</h3>
          <p>
            Your hotel doesn't have a SmartThings location set up yet. 
            To get started with IoT management:
          </p>
          <ol style={{ marginTop: '10px', paddingLeft: '20px' }}>
            <li>Go to your <strong>Rooms Tab</strong> in the hotel configuration</li>
            <li>Click the <strong>"Sync SmartThings"</strong> button</li>
            <li>This will create a SmartThings location for your hotel and sync all rooms</li>
          </ol>
          <button
            onClick={() => navigate('/manager/hotel-config')}
            className="btn-primary"
            style={{ marginTop: '15px' }}
          >
            Go to Hotel Configuration
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏠</div>
          <div className="stat-content">
            <div className="stat-value">{rooms.length}</div>
            <div className="stat-label">Total Rooms</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📱</div>
          <div className="stat-content">
            <div className="stat-value">{devices.length}</div>
            <div className="stat-label">Total Devices</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{devices.filter(d => d.isOnline()).length}</div>
            <div className="stat-label">Online</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-value">{devices.filter(d => !d.isOnline()).length}</div>
            <div className="stat-label">Offline</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search devices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Device Types</option>
          {Object.entries(DEVICE_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={selectedRoom || ''}
          onChange={(e) => setSelectedRoom(e.target.value || null)}
        >
          <option value="">All Rooms</option>
          {rooms.map(room => (
            <option key={room.id} value={room.id}>{room.name}</option>
          ))}
        </select>
      </div>

      {/* Main Content: Rooms & Devices */}
      <div className="iot-content">
        {/* Rooms Sidebar */}
        <div className="rooms-sidebar">
          <h3>Rooms</h3>
          <div className="rooms-list">
            {rooms.map(room => (
              <div
                key={room.id}
                className={`room-item ${selectedRoom === room.id ? 'active' : ''}`}
                onClick={() => setSelectedRoom(room.id === selectedRoom ? null : room.id)}
              >
                <div className="room-header">
                  <MapPin size={16} />
                  <span className="room-name">{room.name}</span>
                </div>
                {room.hotelRoomNumber && (
                  <div className="room-number">Room #{room.hotelRoomNumber}</div>
                )}
                <div className="room-device-count">
                  {getDevicesByRoom(room.id).length} devices
                </div>
                <button
                  className="btn-icon-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRoom(room.id);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Devices Grid */}
        <div className="devices-area">
          <h3>Devices {selectedRoom && `in ${rooms.find(r => r.id === selectedRoom)?.name}`}</h3>
          <div className="devices-grid">
            {filteredDevices.map(device => (
              <div 
                key={device.id} 
                className={`device-card ${device.isOnline() ? 'online' : 'offline'}`}
                onClick={() => navigate(`/device-details/${device.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="device-card-header">
                  <span className="device-icon">{device.getIcon()}</span>
                  <span className={`status-indicator ${device.status}`}></span>
                </div>
                <div className="device-card-body">
                  <h4>{device.name}</h4>
                  <p className="device-type">{device.getTypeLabel()}</p>
                  <p className="device-room">
                    {rooms.find(r => r.id === device.roomId)?.name || 'Unassigned'}
                  </p>
                  {device.hasCapability('switch') && (
                    <div className="device-state">
                      Power: <strong>{device.getState('switch', 'switch') || 'unknown'}</strong>
                    </div>
                  )}
                </div>
                <div className="device-card-actions">
                  <button 
                    className="btn-view" 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/device-details/${device.id}`);
                    }}
                    title="View Details"
                  >
                    <Eye size={16} />
                  </button>
                  {device.hasCapability('switch') && (
                    <button
                      className="btn-control"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleDevice(device);
                      }}
                      disabled={!device.isOnline()}
                    >
                      <Power size={16} />
                    </button>
                  )}
                  <button 
                    className="btn-edit" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditDevice(device);
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className="btn-delete" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDevice(device.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {filteredDevices.length === 0 && (
            <div className="empty-state">
              <p>No devices found matching your filters.</p>
            </div>
          )}
        </div>
      </div>

        {/* Subscriptions Section */}
        <div className="subscriptions-section">
          <div className="subscriptions-header">
            <div>
              <h3>SmartThings Subscriptions</h3>
              <p className="subscriptions-subtitle">Stream real-time device events into InnBotics automation tools.</p>
            </div>
            <div className="subscriptions-actions">
              <button
                className="btn-icon"
                onClick={loadSubscriptions}
                disabled={subscriptionsLoading}
              >
                <RefreshCw size={18} className={subscriptionsLoading ? 'spinning' : ''} />
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  resetSubscriptionForm();
                  setShowAddSubscriptionModal(true);
                }}
              >
                <Plus size={18} />
                New Subscription
              </button>
            </div>
          </div>

          {subscriptionsLoading && (
            <div className="subscriptions-loading">
              <div className="spinner-small"></div>
              <p>Syncing with SmartThings…</p>
            </div>
          )}

          {!subscriptionsLoading && subscriptions.length === 0 && (
            <div className="subscriptions-empty">
              <Zap size={28} />
              <p>No subscriptions yet. Create one to start receiving device events.</p>
            </div>
          )}

          {!subscriptionsLoading && subscriptions.length > 0 && (
            <div className="subscriptions-grid">
              {subscriptions.map((subscription) => {
                const subscriptionId = resolveSubscriptionId(subscription);
                const device = subscription.device || {};
                const targetUrl = subscription.targetUrl || subscription.targetUri || DEFAULT_WEBHOOK_URL;

                return (
                  <div key={subscriptionId || subscription.name} className="subscription-card">
                    <div className="subscription-card-header">
                      <div className="subscription-badge">
                        <Zap size={16} />
                        <span>{subscription.type || subscription.sourceType || 'Device'}</span>
                      </div>
                      <span className="subscription-id">{subscriptionId || '—'}</span>
                    </div>
                    <h4 className="subscription-name">{subscription.name || 'Unnamed Subscription'}</h4>
                    <div className="subscription-meta">
                      {device.deviceId && (
                        <div>
                          <span>Device ID</span>
                          <strong>{device.deviceId}</strong>
                        </div>
                      )}
                      {device.componentId && (
                        <div>
                          <span>Component</span>
                          <strong>{device.componentId}</strong>
                        </div>
                      )}
                      {device.capability && (
                        <div>
                          <span>Capability</span>
                          <strong>{device.capability}</strong>
                        </div>
                      )}
                      {device.attribute && (
                        <div>
                          <span>Attribute</span>
                          <strong>{device.attribute}</strong>
                        </div>
                      )}
                      <div>
                        <span>State changes only</span>
                        <strong>{String(device.stateChangeOnly ?? subscription.stateChangeOnly ?? true)}</strong>
                      </div>
                      <div className="subscription-target">
                        <span>Target URL</span>
                        <code>{targetUrl}</code>
                      </div>
                    </div>
                    <div className="subscription-card-actions">
                      <button
                        type="button"
                        className="subscription-remove"
                        onClick={() => handleDeleteSubscription(subscription)}
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* Add Room Modal */}
      {showAddRoomModal && (
        <div className="modal-overlay" onClick={() => setShowAddRoomModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Room</h2>
            <form onSubmit={handleCreateRoom}>
              <div className="form-group">
                <label>Room Name *</label>
                <input
                  type="text"
                  required
                  value={roomForm.name}
                  onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                  placeholder="e.g., Suite 101"
                />
              </div>
              <div className="form-group">
                <label>Hotel Room Number</label>
                <input
                  type="text"
                  value={roomForm.hotelRoomNumber}
                  onChange={(e) => setRoomForm({ ...roomForm, hotelRoomNumber: e.target.value })}
                  placeholder="101"
                />
              </div>
              <div className="form-group">
                <label>Floor</label>
                <input
                  type="text"
                  value={roomForm.floor}
                  onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                  placeholder="1"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddRoomModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Create Room</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <div className="modal-overlay" onClick={() => setShowAddDeviceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Device</h2>
            <form onSubmit={handleAddDevice}>
              <div className="form-group">
                <label>Device ID (from SmartThings) *</label>
                <input
                  type="text"
                  required
                  value={deviceForm.deviceId}
                  onChange={(e) => setDeviceForm({ ...deviceForm, deviceId: e.target.value })}
                  placeholder="Enter SmartThings Device ID"
                />
              </div>
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  required
                  value={deviceForm.name}
                  onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                  placeholder="e.g., Bedroom Light"
                />
              </div>
              <div className="form-group">
                <label>Assign to Room</label>
                <select
                  value={deviceForm.roomId}
                  onChange={(e) => setDeviceForm({ ...deviceForm, roomId: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddDeviceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Add Device</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Device Modal */}
      {showEditDeviceModal && (
        <div className="modal-overlay" onClick={() => setShowEditDeviceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Device</h2>
            <form onSubmit={handleUpdateDevice}>
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  required
                  value={deviceForm.name}
                  onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Assign to Room</label>
                <select
                  value={deviceForm.roomId}
                  onChange={(e) => setDeviceForm({ ...deviceForm, roomId: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditDeviceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Update Device</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Subscription Modal */}
      {showAddSubscriptionModal && (
        <div className="modal-overlay" onClick={() => setShowAddSubscriptionModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Create SmartThings Subscription</h2>
            <form onSubmit={handleCreateSubscription}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Subscription Name</label>
                  <input
                    type="text"
                    value={subscriptionForm.name}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                    placeholder="e.g., Suite 101 Switch Events"
                  />
                </div>
                <div className="form-group">
                  <label>Device ID *</label>
                  <input
                    type="text"
                    required
                    value={subscriptionForm.deviceId}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, deviceId: e.target.value })}
                    placeholder="SmartThings deviceId"
                  />
                </div>
                <div className="form-group">
                  <label>Component</label>
                  <input
                    type="text"
                    value={subscriptionForm.componentId}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, componentId: e.target.value })}
                    placeholder="main"
                  />
                </div>
                <div className="form-group">
                  <label>Capability *</label>
                  <input
                    type="text"
                    required
                    value={subscriptionForm.capability}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, capability: e.target.value })}
                    placeholder="switch, thermostat, contactSensor..."
                  />
                </div>
                <div className="form-group">
                  <label>Attribute *</label>
                  <input
                    type="text"
                    required
                    value={subscriptionForm.attribute}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, attribute: e.target.value })}
                    placeholder="switch, temperature, contact..."
                  />
                </div>
                <div className="form-group">
                  <label>Value Filter</label>
                  <input
                    type="text"
                    value={subscriptionForm.value}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, value: e.target.value })}
                    placeholder="* (any value)"
                  />
                </div>
                <div className="form-group">
                  <label>Target URL *</label>
                  <input
                    type="text"
                    required={!DEFAULT_WEBHOOK_URL}
                    value={subscriptionForm.targetUrl}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, targetUrl: e.target.value })}
                    placeholder="https://your-domain/api/smartthings/events"
                  />
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={subscriptionForm.stateChangeOnly}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, stateChangeOnly: e.target.checked })}
                    />
                    Notify only on state changes
                  </label>
                </div>
              </div>
              <div className="modal-hint">
                <p>Tip: Subscription payloads are logged to <code>logs/smartthings-events.log</code> and broadcast through Socket.IO for real-time dashboards.</p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddSubscriptionModal(false)}
                  disabled={savingSubscription}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={savingSubscription}>
                  {savingSubscription ? 'Creating…' : 'Create Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IoTManagement;
